"use client";

import { useState } from "react";
import Link from "@/components/i18n/link";
import { Bell, CheckCircle2, XCircle, AlertTriangle, PartyPopper } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useNotificationsStore } from "@/lib/store/notifications-store";
import { formatNotificationDate } from "@/lib/notifications";
import { useLocale, useMessages } from "@/lib/i18n/client";
import messages from "./NotificationBell.i18n";

function severityIcon(severity: "ok" | "partial_error" | "error") {
  if (severity === "ok") return <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />;
  if (severity === "partial_error") return <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />;
  return <XCircle className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />;
}

// items viene precargado por NotificationsPoller (fetch silencioso al montar
// + cada vez que cambia unreadCount) — abrir el dropdown nunca dispara un
// fetch ni muestra loading, solo lee lo que ya está en el store.
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const unreadCount = useNotificationsStore((s) => s.unreadCount);
  const items = useNotificationsStore((s) => s.items);
  const t = useMessages(messages);
  const lang = useLocale();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        aria-label={t.title}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground transition hover:bg-muted hover:text-foreground"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-destructive-foreground">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="border-b border-border px-4 py-3">
          <p className="text-sm font-semibold">{t.title}</p>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items === null && (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">{t.loading}</p>
          )}
          {items?.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">
              {t.empty}
            </p>
          )}
          {items?.map((n) => (
            <Link
              key={n._id}
              href={`/notificaciones/${n._id}`}
              onClick={() => setOpen(false)}
              className="relative flex items-start gap-2.5 px-4 py-3 text-sm transition hover:bg-muted"
            >
              {!n.read && (
                <span className="absolute right-3 top-3 h-2 w-2 shrink-0 rounded-full bg-primary" />
              )}
              {n.kind === "panorama_invite" ? (
                <>
                  <PartyPopper className="h-4 w-4 shrink-0 text-primary" />
                  <div className="min-w-0 flex-1 pr-3">
                    <p className="truncate font-medium">
                      {t.invited(n.creatorName, n.panoramaTitle)}
                    </p>
                    <p className="text-[11px] text-muted-foreground">{formatNotificationDate(n.createdAt, lang)}</p>
                  </div>
                </>
              ) : (
                <>
                  {severityIcon(n.severity)}
                  <div className="min-w-0 flex-1 pr-3">
                    <p className="truncate font-medium">{n.integrationName}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {t.syncSummary(n.statsSummary.created, n.statsSummary.updated, n.statsSummary.failed)}
                    </p>
                    <p className="text-[11px] text-muted-foreground">{formatNotificationDate(n.finishedAt, lang)}</p>
                  </div>
                </>
              )}
            </Link>
          ))}
        </div>
        <Link
          href="/notificaciones"
          onClick={() => setOpen(false)}
          className="block border-t border-border px-4 py-2.5 text-center text-sm font-medium text-primary hover:bg-muted"
        >
          {t.seeAll}
        </Link>
      </PopoverContent>
    </Popover>
  );
}
